import type { DashboardRoute } from "#/types/navigation";
import { Button } from "@mantine/core";
import { IconChevronDown } from "@tabler/icons-react";

import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

interface Props {
  route: DashboardRoute;
  onClick: () => void;
}

const SideNavButton = ({ route, onClick }: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [opened, setOpened] = useState<boolean>(false);

  useEffect(() => {
    if (
      route?.children &&
      route?.children?.length > 0 &&
      location.pathname.includes(route.href)
    ) {
      setOpened(true);
    } else {
      setOpened(false);
    }
  }, [location.pathname, route]);

  if (route.children && route.children.length > 0) {
    return (
      <div
        className={`w-full flex flex-col transition-all duration-100 ${opened && "bg-white/40 rounded-lg"}`}
      >
        <Button
          onClick={() => {
            setOpened((prev) => !prev);
          }}
          fullWidth
          classNames={{
            root: `flex justify-between items-center0  px-4 h-auto min-h-12 transition-all hover:bg-client-color/10 whitespace-normal ${
              opened
                ? "bg-client-color/40 border-b border-b-white"
                : "bg-transparent "
            }`,
            label: `text-left break-words`,
          }}
          leftSection={
            <div
              className={`transition-all duration-300 flex-shrink-0 ${opened ? "rotate-0" : "-rotate-90"}`}
            >
              <IconChevronDown size={20} stroke={2.0} />
            </div>
          }
        >
          {t(route.label)}
        </Button>

        {opened && (
          <div className="px-2 py-2 w-full flex flex-col gap-2">
            {route.children.map((item) => {
              const fullPath = `${route.href}${item.href}`;
              const active = location.pathname.includes(fullPath);
              return (
                <Button
                  key={item.href}
                  size="xs"
                  onClick={() => {
                    void navigate(fullPath);
                    onClick();
                  }}
                  fullWidth
                  autoContrast
                  classNames={{
                    root: `flex justify-between items-center0  px-4 h-auto min-h-10 rounded-full transition-colors whitespace-normal ${
                      active
                        ? "bg-white text-black hover:bg-client-color/30"
                        : "bg-transparent hover:bg-client-color/30 text-black"
                    }`,
                    label: `text-left break-words`,
                  }}
                  leftSection={<item.icon size={20} stroke={2.0} />}
                >
                  {t(item.label)}
                </Button>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  return (
    <Button
      onClick={() => {
        void navigate(route.href);
        onClick();
      }}
      fullWidth
      classNames={{
        root: `flex justify-between items-center0  px-4 h-auto min-h-12 rounded-full transition-colors whitespace-normal ${
          location.pathname === route.href
            ? "bg-white text-black hover:bg-white hover:text-black"
            : "bg-transparent hover:bg-white/20"
        }`,
        label: `text-left break-words`,
      }}
      leftSection={<route.icon size={20} stroke={2.0} />}
    >
      {t(route.label)}
    </Button>
  );
};

export default SideNavButton;
